import React, { useState } from "react";
import { Button, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { PricingPlanProps } from "../../types/PricingPlanProps";
import {
  PhoneNumberObj,
  TwilioPhoneCatalogPicker,
} from "../whatsappBusinessAccount/onBoardingProcess/twilioPhoneNumberSelector/TwilioPhoneCatalogPicker";
import { TwilioPhoneByonPicker } from "../whatsappBusinessAccount/onBoardingProcess/twilioPhoneNumberSelector/TwilioPhoneByonPicker";
import { Step2MessageType, Step2PhoneState } from "../../types/copilot/step2";

type UILang = "en" | "he";

const PHONE_TR = {
  en: {
    use_own: "Use my own number",
    own_note1: "Use any WhatsApp-supported region.",
    own_note2: "It must be clean (no active WhatsApp account, business or personal).",
    own_note2_tooltip: "If this number is already connected to a WhatsApp account (business or personal), please use a different number or disconnect it from WhatsApp before continuing.",
    own_note3: "It must be able to receive SMS verification messages.",
    own_note3_tooltip: "VoIP, virtual, or internet-based numbers often fail WhatsApp verification. Please use a regular mobile number that can reliably receive SMS, or simply choose a number from Vuelve's collection instead.",
    get_vuelve: "Get a number from Vuelve",
    plan_includes_iso: (iso: string) => `Your plan includes a free ${iso} number.`,
    country_code_note: "The country code does not affect messaging costs or delivery, it only changes how your number looks in WhatsApp.",
    plan_includes_multi: "Basic plans include a free US phone number.",
    enter_own: "Great. Enter the phone number you'd like to use.",
    send_code_disabled: "To continue, enter a valid phone number and confirm both conditions. Without that, WhatsApp will reject connecting this number to the new account.",
    send_verification: "Send verification code",
    switch_vuelve: "Switch to Vuelve numbers",
    sms_sent: "We sent an SMS code to your number. Enter it to verify ownership.",
    verify_code: "Verify code",
    resend_code: "Resend code",
    sms_failed: "That number couldn't be verified. Make sure it can receive SMS and try again, or choose a number from Vuelve.",
    try_again: "Try again",
    whatsapp_check: "Now we check that this number is not already connected to WhatsApp (placeholder).",
    mock_check_passed: "Mock: check passed",
    mock_already_connected: "Mock: already connected",
    already_connected: "This number is already connected to an active WhatsApp account. Use a different number, or choose one from Vuelve.",
    use_different: "Use a different number",
    verified_saved: "Perfect. Your number was verified and saved successfully.",
    ready_to_use: "Perfect. Your number is ready to use.",
    continue_meta: "Continue to Meta connection",
    pick_number: "Let's pick a number for your new WhatsApp account.",
    pick_number_no_plan_note: "US phone numbers are included in our basic plans at no extra cost. Choosing a number from another country may require a higher-tier plan.",
    use_this: "Use this number",
    switch_own: "Switch to my own number",
    tries_left: (n: number) => `(${n} tries left)`,
  },
  he: {
    use_own: "השתמש במספר שלי",
    own_note1: "השתמשו בכל אזור הנתמך על ידי WhatsApp.",
    own_note2: "המספר חייב להיות נקי (ללא חשבון WhatsApp פעיל, עסקי או אישי).",
    own_note2_tooltip: "אם מספר זה כבר מחובר לחשבון WhatsApp (עסקי או אישי), אנא השתמשו במספר אחר או נתקו אותו מ-WhatsApp לפני שתמשיכו.",
    own_note3: "המספר חייב להיות מסוגל לקבל הודעות SMS לאימות.",
    own_note3_tooltip: "מספרי VoIP, וירטואליים או אינטרנטיים לעיתים קרובות נכשלים באימות WhatsApp. אנא השתמשו במספר נייד רגיל שיכול לקבל SMS באופן אמין, או פשוט בחרו מספר מאוסף Vuelve.",
    get_vuelve: "קבל מספר מ-Vuelve",
    plan_includes_iso: (iso: string) => `התוכנית שלכם כוללת מספר ${iso} חינמי.`,
    country_code_note: "קידומת המדינה לא משפיעה על עלויות השליחה או המסירה, היא רק משנה כיצד המספר שלכם נראה ב-WhatsApp.",
    plan_includes_multi: "תוכניות הבסיס כוללות מספר טלפון אמריקאי חינמי.",
    enter_own: "מצוין. הזינו את מספר הטלפון שתרצו להשתמש בו.",
    send_code_disabled: "להמשך, הזינו מספר טלפון תקין ואשרו את שני התנאים. ללא כך, WhatsApp יסרב לחבר מספר זה לחשבון החדש.",
    send_verification: "שלח קוד אימות",
    switch_vuelve: "עברו למספרי Vuelve",
    sms_sent: "שלחנו קוד SMS למספרכם. הזינו אותו לאימות הבעלות.",
    verify_code: "אמת קוד",
    resend_code: "שלח קוד מחדש",
    sms_failed: "לא ניתן היה לאמת מספר זה. וודאו שהוא יכול לקבל SMS ונסו שוב, או בחרו מספר מ-Vuelve.",
    try_again: "נסה שוב",
    whatsapp_check: "כעת אנו בודקים שמספר זה אינו מחובר כבר ל-WhatsApp (placeholder).",
    mock_check_passed: "מדומה: עבר בדיקה",
    mock_already_connected: "מדומה: כבר מחובר",
    already_connected: "מספר זה כבר מחובר לחשבון WhatsApp פעיל. השתמשו במספר אחר, או בחרו אחד מ-Vuelve.",
    use_different: "השתמש במספר אחר",
    verified_saved: "מצוין. מספרכם אומת ונשמר בהצלחה.",
    ready_to_use: "מצוין. מספרכם מוכן לשימוש.",
    continue_meta: "המשך לחיבור Meta",
    pick_number: "בואו נבחר מספר לחשבון ה-WhatsApp החדש שלכם.",
    pick_number_no_plan_note: "מספרי טלפון אמריקאיים כלולים בתוכניות הבסיסיות שלנו ללא עלות נוספת. בחירת מספר ממדינה אחרת עשויה לדרוש שדרוג לתוכנית ברמה גבוהה יותר.",
    use_this: "השתמש במספר זה",
    switch_own: "עברו למספר משלי",
    tries_left: (n: number) => `(נותרו ${n} ניסיונות)`,
  },
};

type Props = {
  lang?: UILang;
  type: Step2MessageType;
  canInteract: boolean;
  hasRegisteredPhone: boolean;
  hasPlan: boolean;
  currentPricingPlan?: PricingPlanProps | null;
  selectedCatalogPhoneNumber: PhoneNumberObj | null;
  setSelectedCatalogPhoneNumber: (number: PhoneNumberObj | null) => void;
  selectedByonPhoneNumber: PhoneNumberObj | null;
  setSelectedByonPhoneNumber: (number: PhoneNumberObj | null) => void;
  isSavingPhoneSelection: boolean;
  isSendingByonCode: boolean;
  isVerifyingByonCode: boolean;
  byonCode: string;
  setByonCode: (code: string) => void;
  byonVerifyError: string | null;
  byonVerifyAttempts: number;
  isStep2TypeActive: (type: Step2MessageType) => boolean;
  setPhoneChoice: (choice: "purchase" | "bring") => void;
  goStep2: (state: Step2PhoneState) => void;
  setPhoneStepCompleted: (value: boolean) => void;
  updateCatalogPhone: () => Promise<boolean | undefined>;
  sendByonCode: () => Promise<boolean>;
  verifyByonCode: () => Promise<"success" | "failed" | "restart">;
};

const Step2PhoneFlowBubble = ({
  lang = "en",
  type,
  canInteract,
  hasRegisteredPhone,
  hasPlan,
  currentPricingPlan,
  selectedCatalogPhoneNumber,
  setSelectedCatalogPhoneNumber,
  selectedByonPhoneNumber,
  setSelectedByonPhoneNumber,
  isSavingPhoneSelection,
  isSendingByonCode,
  isVerifyingByonCode,
  byonCode,
  setByonCode,
  byonVerifyError,
  byonVerifyAttempts,
  isStep2TypeActive,
  setPhoneChoice,
  goStep2,
  setPhoneStepCompleted,
  updateCatalogPhone,
  sendByonCode,
  verifyByonCode,
}: Props) => {
  const tr = PHONE_TR[lang];
  const [isByonConfirmed, setIsByonConfirmed] = useState(false);
  const [catalogPhoneRegistered, setCatalogPhoneRegistered] = useState(false);
  const disabled = !canInteract || !isStep2TypeActive(type);
  const byonDigitsCount = (selectedByonPhoneNumber?.phoneNumber || "").replace(/\D/g, "").length;
  const isByonPhoneValid = Boolean(selectedByonPhoneNumber?.isoCountry && byonDigitsCount >= 7);


  if (type === "whatsapp_choice") {
    return (
      <div className="v2oa-code-block phone-selection">
        <div className="phone-selection-option">
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => {
              setSelectedCatalogPhoneNumber(null);
              setPhoneChoice("bring");
              goStep2("S2_OWN_FORM");
            }}
          >
            {tr.use_own}
          </Button>
          <ul className="option-note">
            <li>{tr.own_note1}</li>
            <li>
              {tr.own_note2}{" "}
              <Tooltip title={tr.own_note2_tooltip}>
                <InfoCircleOutlined />
              </Tooltip>
            </li>
            <li>
              {tr.own_note3}{" "}
              <Tooltip title={tr.own_note3_tooltip}>
                <InfoCircleOutlined />
              </Tooltip>
            </li>
          </ul>
        </div>
        <div className="phone-selection-option">
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => {
              setSelectedByonPhoneNumber(null);
              setPhoneChoice("purchase");
              goStep2("S2_VUELVE_PICK");
            }}
          >
            {tr.get_vuelve}
          </Button>
          {currentPricingPlan?.includedPhoneNumberIso ? (
            <ul className="option-note">
              <li>{tr.plan_includes_iso(currentPricingPlan.includedPhoneNumberIso)}</li>
              <li>{tr.country_code_note}</li>
            </ul>
          ) : (
            <div className="option-note">
              <div>{tr.plan_includes_multi}</div>
              <div>{tr.country_code_note}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (type === "whatsapp_own_form") {
    const isSendCodeDisabled = disabled || !isByonPhoneValid || !isByonConfirmed;
    const sendCodeDisabledReason = isSendCodeDisabled ? tr.send_code_disabled : "";
    return (
      <div className="v2oa-code-block">
        <div className="v2oa-text">{tr.enter_own}</div>
        <div className="v2oa-complex-content">
          <TwilioPhoneByonPicker
            lang={lang}
            onNumberChange={(payload, isApproved) => {
              setSelectedByonPhoneNumber(payload);
              setIsByonConfirmed(Boolean(isApproved));
            }}
            disabled={disabled}
          />
        </div>
        <div className="v2oa-actions">
          <Tooltip title={sendCodeDisabledReason}>
            <span>
              <Button
                type="primary"
                disabled={isSendCodeDisabled}
                loading={isSendingByonCode}
                onClick={async () => {
                  const ok = await sendByonCode();
                  if (ok) goStep2("S2_OWN_SMS_CODE");
                }}
              >
                {tr.send_verification}
              </Button>
            </span>
          </Tooltip>
          <Button
            type="default"
            disabled={disabled}
            className="link"
            onClick={() => {
              setPhoneChoice("purchase");
              goStep2("S2_VUELVE_PICK");
            }}
          >
            {tr.switch_vuelve}
          </Button>
        </div>
      </div>
    );
  }

  if (type === "whatsapp_own_sms_code") {
    return (
      <div className="v2oa-code-block">
        <div className="v2oa-text">{tr.sms_sent}</div>
        <div className="v2oa-complex-content">
          <input
            className="v2oa-code-input"
            value={canInteract ? byonCode : ""}
            onChange={(e) => setByonCode(e.target.value)}
            disabled={disabled || isVerifyingByonCode}
          />
        </div>
        {canInteract && byonVerifyError ? (
          <div className="v2oa-text">
            {byonVerifyError} {tr.tries_left(Math.max(0, 3 - byonVerifyAttempts))}
          </div>
        ) : null}
        <div className="v2oa-actions">
          <Button
            type="primary"
            disabled={disabled || byonCode.trim().length < 4}
            loading={isVerifyingByonCode}
            onClick={async () => {
              const result = await verifyByonCode();
              if (result === "success") {
                goStep2("S2_OWN_SUCCESS");
              } else if (result === "restart") {
                goStep2("S2_OWN_FORM");
              }
            }}
          >
            {tr.verify_code}
          </Button>
          <Button
            type="default"
            disabled={disabled}
            loading={isSendingByonCode}
            onClick={async () => {
              await sendByonCode();
            }}
          >
            {tr.resend_code}
          </Button>
          <Button
            type="default"
            disabled={disabled}
            className="link"
            onClick={() => {
              setPhoneChoice("purchase");
              goStep2("S2_VUELVE_PICK");
            }}
          >
            {tr.switch_vuelve}
          </Button>
        </div>
      </div>
    );
  }

  if (type === "whatsapp_own_sms_failed") {
    return (
      <div className="v2oa-code-block">
        <div className="v2oa-text">{tr.sms_failed}</div>
        <div className="v2oa-actions">
          <Button type="primary" disabled={disabled} onClick={() => goStep2("S2_OWN_FORM")}>
            {tr.try_again}
          </Button>
          <Button
            type="default"
            disabled={disabled}
            className="link"
            onClick={() => {
              setPhoneChoice("purchase");
              goStep2("S2_VUELVE_PICK");
            }}
          >
            {tr.get_vuelve}
          </Button>
        </div>
      </div>
    );
  }

  if (type === "whatsapp_own_whatsapp_check") {
    return (
      <div className="v2oa-code-block">
        <div className="v2oa-text">{tr.whatsapp_check}</div>
        <div className="v2oa-actions">
          <Button type="primary" disabled={disabled} onClick={() => goStep2("S2_OWN_SUCCESS")}>
            {tr.mock_check_passed}
          </Button>
          <Button type="default" disabled={disabled} onClick={() => goStep2("S2_OWN_WHATSAPP_FAILED")}>
            {tr.mock_already_connected}
          </Button>
        </div>
      </div>
    );
  }

  if (type === "whatsapp_own_whatsapp_failed") {
    return (
      <div className="v2oa-code-block">
        <div className="v2oa-text">{tr.already_connected}</div>
        <div className="v2oa-actions">
          <Button type="primary" disabled={disabled} onClick={() => goStep2("S2_OWN_FORM")}>
            {tr.use_different}
          </Button>
          <Button
            type="default"
            disabled={disabled}
            onClick={() => {
              setPhoneChoice("purchase");
              goStep2("S2_VUELVE_PICK");
            }}
          >
            {tr.get_vuelve}
          </Button>
        </div>
      </div>
    );
  }

  if (type === "whatsapp_own_success") {
    if (hasRegisteredPhone) {
      return (
        <div className="v2oa-code-block">
          <div className="v2oa-text">{tr.verified_saved}</div>
        </div>
      );
    }
    return (
      <div className="v2oa-code-block">
        <div className="v2oa-text">{tr.ready_to_use}</div>
        <Button
          type="primary"
          disabled={disabled}
          onClick={() => {
            setPhoneStepCompleted(true);
            goStep2("S2_DONE");
          }}
        >
          {tr.continue_meta}
        </Button>
      </div>
    );
  }

  if (type === "whatsapp_vuelve_pick") {
    if (catalogPhoneRegistered) {
      return (
        <div className="v2oa-code-block">
          <div className="v2oa-text">{tr.ready_to_use}</div>
        </div>
      );
    }
    return (
      <div className="v2oa-code-block v2oa-vuelve-pick">
        <div className="v2oa-text">{tr.pick_number}</div>
        {!currentPricingPlan && <div className="v2oa-note">{tr.pick_number_no_plan_note}</div>}
        <div className="v2oa-complex-content">
          <TwilioPhoneCatalogPicker
            onNumberChange={setSelectedCatalogPhoneNumber}
            disabled={disabled}
          />
        </div>
        <div className="v2oa-actions">
          <Button
            type="primary"
            disabled={!selectedCatalogPhoneNumber?.phoneNumber || disabled}
            loading={isSavingPhoneSelection}
            onClick={async () => {
              const ok = await updateCatalogPhone();
              if (ok) {
                setCatalogPhoneRegistered(true);
                setPhoneStepCompleted(true);
                if (hasPlan) goStep2("S2_DONE");
              }
            }}
          >
            {tr.use_this}
          </Button>
          <Button
            type="default"
            disabled={disabled}
            className="link"
            onClick={() => {
              setPhoneChoice("bring");
              goStep2("S2_OWN_FORM");
            }}
          >
            {tr.switch_own}
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default Step2PhoneFlowBubble;
